import React, { useEffect, useState, useMemo } from "react";
import {
  Drawer,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Close } from "@dreamhub/designsystem/icons";
import { DealData } from "@/pages/deals/types";
import { CompanyData } from "@/pages/companies/types";
import { PersonData } from "@/entities/people/types";
import { UserData } from "@/entities/users/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { LeadData } from "@/pages/leads/types";
import { ContractData } from "@/pages/contracts/types";
import { useLeadActions } from "@/services/hooks/entities/lead/useLeadActions";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import { usePersonActions } from "@/services/hooks/entities/person/usePersonActions";
import { useCompanyActions } from "@/services/hooks/entities/company/useCompanyActions";
import { useDealActions } from "@/services/hooks/entities/deal/useDealActions";
import { useContractActions } from "@/services/hooks/entities/contract/useContractActions";
import { usePartnerActions } from "@/services/hooks/entities/partner/usePartnerActions";
import { usePartnerContactActions } from "@/services/hooks/entities/partnerContact/usePartnerContactActions";
import { PartnerData } from "@/pages/partners/types";
import { PartnerContactData } from "@/pages/partners/contacts/types";
import { EntityData } from "@/services/api/types";
import DealQuickViewContent from "@/components/layouts/QuickView/Deal/DealQuickViewContent";
import CompanyQuickViewContent from "@/components/layouts/QuickView/Company/CompanyQuickViewContent";
import { IconButton } from "@/components/ui/icon-button";
import GuardrailsFailureModal from "@/components/layouts/GuardrailsFailureModal/GuardrailsFailureModal";
import { GuardrailsModalState } from "@/components/layouts/GuardrailsFailureModal/types";
import WidgetSkeleton from "@/components/ui/widgetSkeleton";
import NewQuickViewLoadingState from "@/components/layouts/NewQuickView/NewQuickViewLoadingState";
import { useFullEntityData } from "@/components/layouts/NewQuickView/useFullEntityData";
import PersonQuickViewContent from "@/components/layouts/QuickView/Person/PersonQuickViewContent";
import LeadQuickViewContent from "@/components/layouts/QuickView/Lead/LeadQuickViewContent";
import ContractQuickViewContent from "@/components/layouts/QuickView/Contract/ContractQuickViewContent";
import PartnerQuickViewContent from "@/components/layouts/QuickView/Partner/PartnerQuickViewContent";
import PartnerContactQuickViewContent from "@/components/layouts/QuickView/PartnerContact/PartnerContactQuickViewContent";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import QuickViewTitle from "@/components/layouts/QuickView/QuickViewTitle";

interface NewQuickViewContentProps {
  entityId: string;
  entityType: EntityType;
  onClose: () => void;
  handleOpenQuestionnaire?: (questionnaireId?: string) => void;
  isKanbanLayout?: boolean;
  onEntityUpdate?: (updatedEntity: EntityData) => void;
  userData?: UserData;
  isTabView?: boolean;
}

const NewQuickViewContent: React.FC<NewQuickViewContentProps> = ({
  entityId,
  entityType,
  onClose,
  handleOpenQuestionnaire,
  isKanbanLayout = false,
  onEntityUpdate,
  userData,
  isTabView = false
}) => {
  const { entity: fullEntity, isLoading } = useFullEntityData(
    entityId,
    entityType
  );

  const [view, setView] = useState<"deals" | "companies" | "people">("deals");

  const { isDropdownClosing } = useDropdownClosing();
  const [guardrailsModalState, setGuardrailsModalState] =
    useState<GuardrailsModalState>({
      isOpen: false,
      dealId: null,
      deal: null,
      errorData: null
    });

  const handleGuardrailsError = (dealId: string, errorData: any) => {
    setGuardrailsModalState({
      isOpen: true,
      dealId,
      deal: fullEntity as DealData,
      errorData
    });
  };

  const {
    deal,
    getTopOptions: getDealTopOptions,
    getOptions: getDealOptions,
    renderModals: renderDealRelatedModals,
    onUpdateField: onUpdateDealField,
    handleOpenCloseDealModal
  } = useDealActions(
    entityType === EntityType.DEAL ? (fullEntity as DealData) : undefined,
    isKanbanLayout,
    false, // isDetailedView
    onClose,
    handleGuardrailsError
  );

  const {
    lead,
    getTopOptions: getLeadTopOptions,
    getOptions: getLeadOptions,
    handleUnmarkAsStuck,
    handleOpenDisqualifyLeadModal,
    renderModals: renderLeadRelatedModals,
    onUpdateField: onUpdateLeadField
  } = useLeadActions(
    entityType === EntityType.LEAD ? (fullEntity as LeadData) : undefined,
    true // isOpen
  );

  const {
    person,
    getTopOptions: getPersonTopOptions,
    getOptions: getPersonOptions,
    renderModals: renderPersonRelatedModals,
    onUpdateField: onUpdatePersonField,
    onCreateLead,
    onCreateDeal,
    onCreateContract
  } = usePersonActions(
    entityType === EntityType.PERSON ? (fullEntity as PersonData) : undefined,
    userData,
    isTabView,
    true // isOpen
  );

  const {
    company,
    getTopOptions: getCompanyTopOptions,
    getOptions: getCompanyOptions,
    renderModals: renderCompanyRelatedModals,
    onUpdateField: onUpdateCompanyField,
    getCreateOptions: getCompanyCreateOptions
  } = useCompanyActions(
    entityType === EntityType.COMPANY ? (fullEntity as CompanyData) : undefined,
    true // isOpen
  );

  const {
    contract,
    getTopOptions: getContractTopOptions,
    getOptions: getContractOptions,
    renderModals: renderContractRelatedModals,
    onUpdateField: onUpdateContractField
  } = useContractActions(
    entityType === EntityType.CONTRACT ? (fullEntity as ContractData) : undefined
  );

  const {
    partner,
    getOptions: getPartnerOptions,
    getCreateOptions: getPartnerCreateOptions,
    renderModals: renderPartnerRelatedModals,
    onUpdateField: onUpdatePartnerField
  } = usePartnerActions(
    entityType === EntityType.PARTNER ? (fullEntity as PartnerData) : undefined,
    true // forceRefetch
  );

  const {
    partnerContact,
    getOptions: getPartnerContactOptions,
    renderModals: renderPartnerContactRelatedModals,
    onUpdateField: onUpdatePartnerContactField,
    onCreateLead: onCreatePartnerContactLead,
    onCreateDeal: onCreatePartnerContactDeal,
    onCreateContract: onCreatePartnerContactContract
  } = usePartnerContactActions(
    entityType === EntityType.PARTNER_CONTACT
      ? (fullEntity as PartnerContactData)
      : undefined,
    userData,
    isTabView,
    true // forceRefetch
  );

  // The entity from action hooks (which may have optimistic updates applied)
  const currentEntity = useMemo(() => {
    switch (entityType) {
      case EntityType.DEAL:
        return deal;
      case EntityType.LEAD:
        return lead;
      case EntityType.PERSON:
        return person;
      case EntityType.COMPANY:
        return company;
      case EntityType.CONTRACT:
        return contract;
      case EntityType.PARTNER:
        return partner;
      case EntityType.PARTNER_CONTACT:
        return partnerContact;
      default:
        return undefined;
    }
  }, [
    entityType,
    deal,
    lead,
    person,
    company,
    contract,
    partner,
    partnerContact
  ]);

  useEffect(() => {
    if (onEntityUpdate && currentEntity) {
      onEntityUpdate(currentEntity);
    }
  }, [currentEntity, onEntityUpdate]);

  const getTopOptions = (): DetailedViewOptions => {
    switch (entityType) {
      case EntityType.DEAL:
        return getDealTopOptions(true);
      case EntityType.LEAD:
        return getLeadTopOptions(true);
      case EntityType.PERSON:
        return getPersonTopOptions();
      case EntityType.COMPANY:
        return getCompanyTopOptions();
      case EntityType.CONTRACT:
        return getContractTopOptions();
      default:
        return {};
    }
  };

  const getOptions = (): DetailedViewOptions => {
    switch (entityType) {
      case EntityType.DEAL:
        return getDealOptions(true, deal?.id);
      case EntityType.LEAD:
        return getLeadOptions(true, lead?.id);
      case EntityType.PERSON:
        return getPersonOptions(true, person?.id);
      case EntityType.COMPANY:
        return getCompanyOptions(true, company?.id);
      case EntityType.CONTRACT:
        return getContractOptions();
      case EntityType.PARTNER:
        return getPartnerOptions(true, partner?.id);
      case EntityType.PARTNER_CONTACT:
        return getPartnerContactOptions(true, partnerContact?.id);
      default:
        return {};
    }
  };

  const renderContent = (): JSX.Element | null => {
    switch (entityType) {
      case EntityType.DEAL:
        return (
          <DealQuickViewContent
            deal={deal}
            onUpdateDealField={onUpdateDealField}
            handleOpenCloseDealModal={handleOpenCloseDealModal}
            handleOpenQuestionnaire={handleOpenQuestionnaire}
            isDrawerOpen={true}
          />
        );
      case EntityType.LEAD:
        return (
          <LeadQuickViewContent
            lead={lead}
            onUpdateLeadField={onUpdateLeadField}
            handleUnmarkAsStuck={handleUnmarkAsStuck}
            handleOpenDisqualifyLeadModal={handleOpenDisqualifyLeadModal}
            setView={setView}
          />
        );
      case EntityType.PERSON:
        return (
          <PersonQuickViewContent
            person={person}
            onUpdatePersonField={onUpdatePersonField}
            onCreateLead={onCreateLead}
            onCreateDeal={onCreateDeal}
            onCreateContract={onCreateContract}
          />
        );
      case EntityType.COMPANY:
        return (
          <CompanyQuickViewContent
            company={company}
            onUpdateCompanyField={onUpdateCompanyField}
            isDrawerOpen={true}
            getCreateOptions={getCompanyCreateOptions}
          />
        );
      case EntityType.CONTRACT:
        return (
          <ContractQuickViewContent
            contract={contract}
            onUpdateContractField={onUpdateContractField}
          />
        );
      case EntityType.PARTNER:
        return (
          <PartnerQuickViewContent
            partner={partner}
            onUpdatePartnerField={onUpdatePartnerField}
            getCreateOptions={getPartnerCreateOptions}
          />
        );
      case EntityType.PARTNER_CONTACT:
        return (
          <PartnerContactQuickViewContent
            partnerContact={partnerContact}
            onUpdatePartnerContactField={onUpdatePartnerContactField}
            onCreateLead={onCreatePartnerContactLead}
            onCreateDeal={onCreatePartnerContactDeal}
            onCreateContract={onCreatePartnerContactContract}
          />
        );
      default:
        return null;
    }
  };

  const setIsGuardrailsModalOpen: React.Dispatch<
    React.SetStateAction<boolean>
  > = (value) => {
    setGuardrailsModalState((prevState) => ({
      ...prevState,
      isOpen: typeof value === "function" ? value(prevState.isOpen) : value
    }));
  };

  const renderModals = (): JSX.Element | null => {
    switch (entityType) {
      case EntityType.DEAL:
        return (
          <>
            {renderDealRelatedModals(isDropdownClosing, true, onClose)}
            <GuardrailsFailureModal
              isOpen={guardrailsModalState.isOpen && !isDropdownClosing}
              setIsOpen={setIsGuardrailsModalOpen}
              dealId={guardrailsModalState.dealId}
              deal={guardrailsModalState.deal}
              errorData={guardrailsModalState.errorData}
            />
          </>
        );
      case EntityType.LEAD:
        return renderLeadRelatedModals(
          isDropdownClosing,
          view,
          setView,
          true,
          onClose
        );
      case EntityType.PERSON:
        return renderPersonRelatedModals(
          isDropdownClosing,
          true,
          onClose,
          userData
        );
      case EntityType.COMPANY:
        return renderCompanyRelatedModals(
          isDropdownClosing,
          true,
          onClose,
          userData
        );
      case EntityType.CONTRACT:
        return renderContractRelatedModals(isDropdownClosing, onClose);
      case EntityType.PARTNER:
        return renderPartnerRelatedModals(
          isDropdownClosing,
          true,
          onClose,
          userData
        );
      case EntityType.PARTNER_CONTACT:
        return renderPartnerContactRelatedModals(
          isDropdownClosing,
          true,
          onClose,
          userData
        );
      default:
        return null;
    }
  };

  const isEntityReady = !isLoading && fullEntity && currentEntity;

  return (
    <>
      <Drawer
        open={true}
        direction="right"
        fixed
        modal={false}
        disablePreventScroll={true}
        noBodyStyles={true}
        dismissible={false}
      >
        <AppDrawerContent>
          <DrawerHeader className="gap-2">
            <DrawerTitle className="block flex-1 min-w-0">
              {!isEntityReady ? (
                <WidgetSkeleton className="h-9 w-full rounded-lg" />
              ) : (
                <QuickViewTitle
                  item={fullEntity}
                  itemType={entityType}
                  currentEntity={currentEntity}
                  isKanbanLayout={isKanbanLayout}
                  isOpen={true}
                  getTopOptions={getTopOptions}
                  getOptions={getOptions}
                  onUpdatePersonField={onUpdatePersonField}
                  onUpdateDealField={onUpdateDealField}
                  onUpdateCompanyField={onUpdateCompanyField}
                  onUpdateLeadField={onUpdateLeadField}
                  onUpdateContractField={onUpdateContractField}
                  onUpdatePartnerField={onUpdatePartnerField}
                  onUpdatePartnerContactField={onUpdatePartnerContactField}
                />
              )}
            </DrawerTitle>
            <IconButton
              data-test="close-quick-view"
              onClick={onClose}
              variant="iconNoBg"
              size="md"
            >
              <Close />
            </IconButton>
          </DrawerHeader>
          <DrawerDescription className="!hidden">Quick view</DrawerDescription>
          {!isEntityReady ? (
            <NewQuickViewLoadingState />
          ) : (
            <div className="flex pr-4 pb-5 pt-4 flex-col w-full overflow-y-auto max-h-full">
              {renderContent()}
            </div>
          )}
        </AppDrawerContent>
      </Drawer>
      {renderModals()}
    </>
  );
};

export default NewQuickViewContent;
